"use client";

import { IUserSubsRefundAmount } from "@src/api/user/user";
import { differenceInDays, format, set } from "date-fns";
import dayjs from "dayjs";
import React from "react";
import { css, styled } from "styled-components";

interface Props {
    subsRefundAmount?: IUserSubsRefundAmount;
    userSubsInfo?: any;
    onCloseView: () => void;
    onClickUnsubs: () => void;
    isWithDraw?: boolean;
}

const TerminateNow: React.FC<Props> = ({ subsRefundAmount, userSubsInfo, onCloseView, onClickUnsubs, isWithDraw = false }) => {
    return (
        <RootBoxStyle>
            <SubsInfoBoxStyle>
                <div className="SubsInfoBox">
                    <p className="Title">내 잔여 구독기간</p>
                    <p className="SubTitleBox">
                        <div className="Date">{format(new Date(subsRefundAmount?.expiredAt ?? 0), "yy년 MM월 dd일까지")}</div>

                        <div className="LeftoverDays">
                            {(subsRefundAmount?.totalDays ?? 0) - (subsRefundAmount?.usedDays ?? 0)}일 남음
                        </div>
                    </p>
                </div>

                {subsRefundAmount?.penaltyType === 5 && (
                    <>
                        <div className="Divider"></div>

                        <div className="SubsInfoBox">
                            <p className="Title">구독 유지기간</p>
                            <p className="SubTitleBox">
                                <div className="Date">
                                    {format(new Date(userSubsInfo?.contractExpireAt ?? 0), "yy년 MM월 dd일까지")}
                                </div>

                                <div className="LeftoverDays">
                                    {dayjs(userSubsInfo?.contractExpireAt).diff(dayjs(subsRefundAmount?.firstPaidAt), "month")}
                                    개월 남음
                                </div>
                            </p>
                        </div>
                    </>
                )}
            </SubsInfoBoxStyle>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%" }}>
                <DescBox>
                    <span className="PointText">
                        {subsRefundAmount?.penaltyType === 5 ? "매월 " : ""}
                        {subsRefundAmount?.benefitPrice?.toLocaleString()}원 할인혜택
                    </span>
                    <span>을받고있어요</span>
                </DescBox>

                <SalePriceInfoBoxStyle>
                    <div className="SalePriceBox">
                        <p>정가</p>
                        <p>{subsRefundAmount?.productPrice?.toLocaleString()}원</p>
                    </div>
                    <div className="SalePriceBox">
                        <p className="SaleBluePoint">할인 금액</p>
                        <p className="SaleBluePoint">{subsRefundAmount?.benefitPrice?.toLocaleString()}원</p>
                    </div>
                    <div className="Divider"></div>
                    <div className="SalePriceBox">
                        <p>{subsRefundAmount?.penaltyType === 5 ? "매월" : "매년"} 결제금액</p>
                        <p className="MonthlyPointText">{subsRefundAmount?.paidPrice?.toLocaleString()}원</p>
                    </div>
                </SalePriceInfoBoxStyle>
            </div>

            <DescBoxStyle>
                <p>지금 해지하시면 약관에 따라</p>
                {subsRefundAmount?.penaltyType !== 5 ? (
                    <p>
                        <span className="RedPoint">
                            {subsRefundAmount?.penaltyPrice?.toLocaleString()}
                            원이 공제
                        </span>
                        되고 환불됩니다
                    </p>
                ) : (
                    <p>
                        <span className="PointText">위약금 </span>
                        <span className="RedPoint">{subsRefundAmount?.penaltyPrice?.toLocaleString()}원</span>
                        <span>이 결제됩니다.</span>
                    </p>
                )}
            </DescBoxStyle>

            <PriceInfoBoxStyle>
                {subsRefundAmount?.penaltyType === 4 && (
                    <>
                        <div className="PriceInfoBox">
                            <p className="PriceText">위약금 (정가 20%)</p>
                            <p>{subsRefundAmount?.penaltyPrice?.toLocaleString()}원</p>
                        </div>
                        <div className="PriceInfoBox">
                            <p className="PriceText">구독 사용금액</p>
                            <p>{subsRefundAmount?.usedDaysPrice?.toLocaleString()}원</p>
                        </div>
                    </>
                )}

                {subsRefundAmount?.penaltyType === 5 && (
                    <>
                        <div className="PriceInfoBox">
                            <p className="PriceText">상품 정가 금액</p>
                            <p>{subsRefundAmount?.productPrice?.toLocaleString()}원</p>
                        </div>
                        <div className="PriceInfoBox">
                            <p className="PriceText">실제 결제 금액</p>
                            <p>{subsRefundAmount?.paidPrice?.toLocaleString()}원</p>
                        </div>
                        <div className="PriceInfoBox">
                            <p className="PriceText">이용월수</p>
                            <p>{subsRefundAmount?.usedMonths}개월</p>
                        </div>
                    </>
                )}

                <div className="Divider"></div>

                <div className="PriceInfoBox FinalPrice">
                    <p>최종 위약금</p>
                    <p>
                        {subsRefundAmount?.penaltyType === 5
                            ? subsRefundAmount?.penaltyPrice?.toLocaleString()
                            : ((subsRefundAmount?.penaltyPrice ?? 0) + (subsRefundAmount?.usedDaysPrice ?? 0))?.toLocaleString()}
                        원
                    </p>
                </div>
            </PriceInfoBoxStyle>

            <div style={{ display: "flex", flexDirection: "column", gap: "14px", width: "100%" }}>
                {subsRefundAmount?.penaltyType === 5 ? (
                    <FooterStyle>
                        위약금 = (상품 정가 금액 - 결제 금액) X 이용월수 <br />
                        구독 해지 시 다음 결제일부터 자동 갱신 중단 <br />
                        결제된 기간까지 서비스 정상 이용 가능 및 환불 불가
                    </FooterStyle>
                ) : (
                    <FooterStyle>
                        <p>사용금액은 정가기준으로 일할계산하여 사용하신 일수만큼 공제됩니다.</p>
                    </FooterStyle>
                )}

                <ContainButtonStyle onClick={onCloseView}>유지하기</ContainButtonStyle>
                <UnSubsTextButtonStyle
                    onClick={() => {
                        return onClickUnsubs();
                    }}
                >
                    {isWithDraw ? "해지 및 탈퇴하기" : "혜택 포기하고 해지하기"}
                </UnSubsTextButtonStyle>
            </div>
        </RootBoxStyle>
    );
};

export default TerminateNow;

const RootBoxStyle = styled.div`
    padding: 14px 14px 28px;
    width: 100%;
    height: calc(100vh - 56px - 24px);
    box-sizing: border-box;
    display: flex;
    align-items: center;
    flex-direction: column;
    gap: 24px;
    overflow-y: auto;
`;

const SubsInfoBoxStyle = styled.div`
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: space-between;
    box-sizing: border-box;
    gap: 12px;
    padding: 14px;

    border-radius: 8px;
    background-color: #f9fafb;

    .SubsInfoBox {
        display: flex;
        flex-direction: column;
        align-items: flex-start;
        justify-content: space-between;
        box-sizing: border-box;
    }

    .Divider {
        width: 100%;
        height: 1px;
        background: #c9c9c9;
    }

    .Title {
        color: #a3a3a3;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 140%;
    }

    .SubTitleBox {
        display: flex;
        flex-direction: row;
        align-items: center;
        gap: 12px;

        .Date {
            color: #0c002c;
            font-size: 16px;
            font-style: normal;
            font-weight: 700;
            line-height: 140%;
        }

        .LeftoverDays {
            color: #2275ee;
            font-size: 12px;
            font-style: normal;
            font-weight: 400;
            line-height: 140%;
        }
    }
`;

const DescBox = styled.div`
    width: 100%;
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: start;

    color: #0c002c;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 26px;

    .PointText {
        color: #0047ff;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px;
    }
`;

const DescBoxStyle = styled.div`
    width: 100%;
    display: flex;
    align-items: flex-start;
    justify-content: flex-start;
    flex-direction: column;

    color: #0c002c;
    font-size: 16px;
    font-style: normal;
    font-weight: 400;
    line-height: 26px;

    .RedPoint {
        color: #f00;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px;
    }

    .PointText {
        color: #000;
        font-size: 18px;
        font-style: normal;
        font-weight: 700;
        line-height: 28px;
    }
`;

const SalePriceInfoBoxStyle = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    gap: 14px;
    padding: 14px 0;

    .SalePriceBox {
        display: flex;
        align-items: center;
        justify-content: space-between;

        color: #0c002c;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 26px;

        .SaleBluePoint {
            color: #0047ff;
            font-size: 16px;
            font-style: normal;
            font-weight: 400;
            line-height: 26px;
        }

        .MonthlyPointText {
            color: #000;
            font-size: 18px;
            font-style: normal;
            font-weight: 700;
            line-height: 28px;
        }
    }

    .Divider {
        height: 1px;
        width: 100%;
        background: #c9c9c9;
    }
`;

const PriceInfoBoxStyle = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    flex-direction: column;
    gap: 8px;
    box-sizing: border-box;
    padding: 14px;

    border-radius: 8px;
    background-color: #fff7f7;

    .PriceInfoBox {
        display: flex;
        align-items: center;
        justify-content: space-between;

        color: #0c002c;
        font-size: 14px;
        font-style: normal;
        font-weight: 400;
        line-height: 26px;

        .PriceText {
            color: #8d8d8d;
            font-size: 14px;
            font-style: normal;
            font-weight: 400;
            line-height: 26px;
        }
    }

    .Divider {
        height: 1px;
        width: 100%;
        background: #c9c9c9;
    }

    .FinalPrice {
        color: #f00;
        font-size: 16px;
        font-style: normal;
        font-weight: 400;
        line-height: 26px;
    }
`;

const FooterStyle = styled.div`
    color: #8d8d8d;
    font-size: 12px;
    font-weight: 500;
    line-height: 20px;
    border-radius: 8px;
    background-color: #f7f8fa;
    padding: 8px 14px;
`;

const BtnBoxStyle = styled.div`
    width: 100%;
`;

const ContainButtonStyle = styled.button`
    margin-top: 10px;
    padding: 14px;
    border-radius: 14px;
    background-color: #2275ee;
    color: #ffffff;
    text-align: center;
    font-size: 18px;
    font-weight: 400;
    line-height: 28px;
    width: 100%;
`;

const UnSubsTextButtonStyle = styled.p`
    color: #8d8d8d;
    text-align: center;
    font-size: 14px;
    font-weight: 400;
    line-height: 24px;
    text-decoration-line: underline;
    text-underline-position: under;
    cursor: pointer;
`;
